import {Chip} from '@dropbox/dig-components/chip';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CloudDownloadLine} from '@dropbox/dig-icons/assets';
import {Column, Table} from 'components/DSYS/Table';
import {CSSProperties, useEffect, useMemo, useState} from 'react';

import {AiQueryCapabilityResult, decodeRecipe, RecipeMode, runRecipe} from './adminAiQueryApi';
import {RenderCell} from './aiQueryCells';
import {ColumnSpec, getProjectionForCapability, TableProjection} from './aiQueryColumns';
import {buildCsv, cellValueToText, csvFilename, downloadCsv} from './aiQueryCsv';

// Rows painted into the DOM per dataset; the CSV chip always delivers the full
// set, so this is purely a render cap to keep a big result from freezing the tab.
const DEFAULT_PREVIEW_ROWS = 200;

// Chip cells never wrap, so their column is simply as wide as the widest chip.
const CHIP_KINDS = new Set(['person', 'personRef', 'personRefs', 'personRow', 'status']);
// Text no longer than this stays on one line (levels, dates, counts, short names).
const NOWRAP_MAX_CHARS = 24;
// Text at least this long reads as prose (about me, previews, messages).
const PROSE_MIN_CHARS = 120;

/**
 * Width bounds for one column's cells, picked from its longest value: short
 * values never wrap, longer text wraps within 120–280px, and prose gets a
 * wider 200–480px measure.
 */
const cellWidthStyle = (column: ColumnSpec, rows: Record<string, unknown>[]): CSSProperties => {
  if (CHIP_KINDS.has(column.kind)) {
    return {};
  }
  const longest = Math.max(0, ...rows.map((row) => cellValueToText(row, column).length));
  if (longest <= NOWRAP_MAX_CHARS) {
    return {whiteSpace: 'nowrap'};
  }
  if (longest >= PROSE_MIN_CHARS) {
    return {minWidth: 200, maxWidth: 480};
  }
  return {minWidth: 120, maxWidth: 280};
};

// Transform verbs: the in-memory assembly steps (combine/derive/aggregate/...).
// When a recipe used any of these, the LAST one is the assembled final table the
// user actually asked for — the earlier capability results are scratch inputs.
const TRANSFORM_CAPABILITIES = new Set([
  'combine_results',
  'derive_columns',
  'aggregate_rows',
  'top_n_per_group',
  'filter_rows',
  'unnest_rows',
  'sort_rows',
]);

/**
 * Pick the dataset(s) to render for a replayed recipe.
 *
 * The server returns every *terminal* dataset (those not consumed by a later
 * transform), which can include exploratory side-lookups. Chat shows one curated
 * answer table, so we mirror that: if the recipe assembled a result with any
 * transform, show only the LAST transform output (the final assembled table);
 * otherwise (a plain single-capability lookup) show every non-empty result.
 */
export const selectFinalDatasets = (
  results: AiQueryCapabilityResult[]
): AiQueryCapabilityResult[] => {
  const nonEmpty = results.filter((r) => r.items && r.items.length > 0);
  const transforms = nonEmpty.filter((r) => TRANSFORM_CAPABILITIES.has(r.capability));
  if (transforms.length > 0) {
    return [transforms[transforms.length - 1]];
  }
  return nonEmpty;
};

/** A single replayed dataset: a heading, a full-set CSV chip, and a preview table. */
export const ResultDataset = ({
  result,
  previewRows = DEFAULT_PREVIEW_ROWS,
  showDownload = true,
  showCapNote = true,
  projection: projectionOverride,
}: {
  result: AiQueryCapabilityResult;
  previewRows?: number;
  showDownload?: boolean;
  /** The "Showing first N rows" note; off when the caller shows its own count. */
  showCapNote?: boolean;
  /** Columns to show instead of the capability's usual projection. */
  projection?: TableProjection;
}) => {
  const defaultProjection = useMemo(
    () => getProjectionForCapability(result.capability, result.items),
    [result.capability, result.items]
  );
  const projection = projectionOverride ?? defaultProjection;
  const rows = result.items.slice(0, previewRows);
  const capped = result.items.length > previewRows;

  // Map the projection's column specs onto DSYS Table columns. The header text
  // doubles as the column `type` (unique within a projection) so the Table's
  // built-in header label renders it verbatim.
  const tableColumns: Column<Record<string, unknown>>[] = useMemo(
    () => projection.columns.map((col) => ({type: col.header, minWidth: 60})),
    [projection.columns]
  );
  // The table sizes columns to their content; these bound how far each grows.
  const cellStyles = useMemo(
    () => projection.columns.map((col) => cellWidthStyle(col, result.items.slice(0, previewRows))),
    [projection.columns, result.items, previewRows]
  );

  if (projection.columns.length === 0 || result.items.length === 0) {
    return null;
  }

  const onDownload = () => downloadCsv(buildCsv(result.items, projection), csvFilename(projection));

  return (
    <Box>
      {showDownload && (
        <Box display="flex" justifyContent="flex-end" marginBottom="8">
          <Chip variant="standard" size="small" onClick={onDownload} aria-label="Download CSV">
            <Chip.IconAccessory>
              <UIIcon src={CloudDownloadLine} size="small" />
            </Chip.IconAccessory>
            <Chip.Content>Download CSV</Chip.Content>
          </Chip>
        </Box>
      )}

      <Table
        columns={tableColumns}
        data={rows}
        borderRadius="Large"
        headerTextSize="xsmall"
        disableHorizontalOverscroll
        columnSizing="content"
      >
        <Table.Header />
        <Table.Body>
          <Table.Rows
            renderRow={(row: Record<string, unknown>) => (
              <Table.Row>
                {projection.columns.map((col, i) => (
                  <Table.Cell key={col.header}>
                    <Box style={cellStyles[i]}>
                      <RenderCell row={row} column={col} />
                    </Box>
                  </Table.Cell>
                ))}
              </Table.Row>
            )}
          />
        </Table.Body>
      </Table>
      {capped && showCapNote && (
        <Text size="xsmall" color="faint" className={atoms({paddingTop: '8'})}>
          Showing first {previewRows.toLocaleString()} rows. Download the CSV for the full set.
        </Text>
      )}
    </Box>
  );
};

/**
 * Decode an encoded recipe and replay it (live or as-of snapshot) under the
 * current user's permissions, rendering the resulting tables. Read-only and
 * self-contained: handles its own loading, error, and empty states so callers
 * (the standalone view page, the home shortcut) just drop it in.
 */
export const RecipeReplay = ({
  encoded,
  mode,
  previewRows,
  showDownload = true,
  emptyMessage = 'This recipe returned no rows for you.',
}: {
  encoded: string;
  mode: RecipeMode;
  previewRows?: number;
  showDownload?: boolean;
  emptyMessage?: string;
}) => {
  const [results, setResults] = useState<AiQueryCapabilityResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    setResults(null);

    let recipe;
    try {
      recipe = decodeRecipe(encoded);
    } catch {
      setError('This view is malformed and could not be read.');
      setIsLoading(false);
      return;
    }

    runRecipe(recipe, mode, {signal: controller.signal})
      .then((r) => setResults(r))
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === 'AbortError') {
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [encoded, mode]);

  const nonEmpty = selectFinalDatasets(results ?? []);

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" style={{gap: 8}}>
        <Spinner size="small" />
        <Text color="subtle">Running…</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        padding="12"
        borderRadius="Medium"
        className={atoms({backgroundColor: 'Background Subtle'})}
      >
        <Text color="error">{error}</Text>
      </Box>
    );
  }

  if (nonEmpty.length === 0) {
    return (
      <Box
        padding="12"
        borderRadius="Medium"
        className={atoms({backgroundColor: 'Background Subtle'})}
      >
        <Text color="subtle">{emptyMessage}</Text>
      </Box>
    );
  }

  return (
    <Stack gap="16">
      {nonEmpty.map((result, i) => (
        <ResultDataset
          key={result.handle ?? `${result.capability}-${i}`}
          result={result}
          previewRows={previewRows}
          showDownload={showDownload}
        />
      ))}
    </Stack>
  );
};
